import qrcode from 'qrcode-generator';

/** Quiet zone the QR spec asks for, in modules. */
const MARGIN = 4;

export interface QrMatrix {
  size: number;
  dark: (row: number, col: number) => boolean;
}

/** Encode text as a QR matrix. Level M survives a scuffed print or a sticker edge. */
export function qrMatrix(text: string): QrMatrix {
  const qr = qrcode(0, 'M');
  qr.addData(text, 'Byte');
  qr.make();
  return { size: qr.getModuleCount(), dark: (r, c) => qr.isDark(r, c) };
}

/** A crisp, scalable SVG (one path, black on white). */
export function qrSvg(text: string): string {
  const m = qrMatrix(text);
  const full = m.size + MARGIN * 2;
  let d = '';
  for (let r = 0; r < m.size; r++) {
    for (let c = 0; c < m.size; c++) {
      if (m.dark(r, c)) d += `M${c + MARGIN} ${r + MARGIN}h1v1h-1z`;
    }
  }
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${full} ${full}" shape-rendering="crispEdges">` +
    `<rect width="${full}" height="${full}" fill="#ffffff"/><path d="${d}" fill="#000000"/></svg>`
  );
}

/** A print-size PNG (about 1200px square). */
export function qrPng(text: string): Promise<Blob> {
  const m = qrMatrix(text);
  const full = m.size + MARGIN * 2;
  const scale = Math.max(8, Math.floor(1200 / full));
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = full * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.reject(new Error('Canvas not available'));
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#000000';
  for (let r = 0; r < m.size; r++) {
    for (let c = 0; c < m.size; c++) {
      if (m.dark(r, c)) ctx.fillRect((c + MARGIN) * scale, (r + MARGIN) * scale, scale, scale);
    }
  }
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not make the PNG'))), 'image/png'),
  );
}

/** Save a Blob as a file. */
export function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
